/**
 * FreezeCap PDP — minimal page JS.
 *
 * Responsibilities:
 *   1. Variant pills inside [data-fcpdp-buy-block] forms — change updates the
 *      hidden id, the price label, the submit availability/copy, and the
 *      sticky bar's variant + price text.
 *   2. Image gallery thumbnails — clicking a thumb scrolls the rail, scroll
 *      events update the active thumb.
 *   3. Variant-help modal — opens a <dialog> describing how to identify
 *      R-134a vs R-1234yf.
 *   4. Sticky ATC bar — visible after the user scrolls past the hero buy
 *      box, hidden inside the final CTA. Tap target smooth-scrolls back
 *      to the canonical hero form.
 *
 * Progressive enhancement: if JS fails, the form still posts to /cart/add
 * with the default variant, the sticky bar stays hidden, the gallery rail
 * still swipes natively, and the modal opens via the browser's native
 * <dialog> behavior.
 */
(function () {
  'use strict';

  /* Idempotency guard — this script may be loaded by both fcpdp-hero AND
     fcpdp-buy-box (different product templates include different sections,
     and some layouts render both). Running init() twice would double-bind
     every listener. Bail if a previous copy already initialized. */
  if (window.__fcpdpProductPageInit) return;
  window.__fcpdpProductPageInit = true;

  /* ---------------------------------------------------------------
     Variant pill wiring — supports both modes:
       • combined: form contains hidden id + variant pills + price +
         submit + payment (used by cost-comparison CTA bar + final CTA)
       • split:    form contains hidden id + submit + payment only;
         variant pills + price live in sibling section blocks identified
         by [data-fcpdp-id-prefix="<prefix>"] (used by the buy-box's
         block-based pattern)
     --------------------------------------------------------------- */
  function wireBuyBlock(form) {
    var hiddenId = form.querySelector('[data-fcpdp-variant-id]');
    var submit   = form.querySelector('[data-fcpdp-submit]');
    var label    = form.querySelector('[data-fcpdp-cta-label]');
    if (!hiddenId) return;

    var idPrefix = form.dataset.idPrefix || '';
    var split    = form.classList.contains('fcpdp-buy-block--split');

    var priceEl, radios;
    if (split && idPrefix) {
      var priceWrap = document.querySelector(
        '[data-fcpdp-price-wrap][data-fcpdp-id-prefix="' + idPrefix + '"]'
      );
      priceEl = priceWrap ? priceWrap.querySelector('[data-fcpdp-price]') : null;
      var picker = document.querySelector(
        '[data-fcpdp-variant-picker][data-fcpdp-id-prefix="' + idPrefix + '"]'
      );
      radios = picker ? picker.querySelectorAll('[data-fcpdp-variant-radio]') : [];
    } else {
      priceEl = form.querySelector('[data-fcpdp-price]');
      radios  = form.querySelectorAll('[data-fcpdp-variant-radio]');
    }
    if (!radios.length) return;

    /* Update button label/state without swapping the element. The submit
       button stays a <button type="submit"> — JS just intercepts the
       click when state is sold-out (see below) and opens the BIS modal
       instead of letting the form post. */
    function setSubmitState(available, variantId) {
      if (!submit) return;
      submit.dataset.state = available ? 'in-stock' : 'sold-out';
      submit.dataset.variantId = variantId;
      // When sold out, force type="button" so a click can't ever submit
      // the form (defense in depth — the click handler also preventDefaults,
      // but switching the type means even a stray submit/Enter can't post
      // /cart/add and trigger a navigation).
      submit.type = available ? 'submit' : 'button';
      if (label) {
        label.textContent = available
          ? submit.dataset.defaultLabel
          : submit.dataset.notifyLabel;
      }
      if (paymentEl) {
        if (available) paymentEl.removeAttribute('hidden');
        else paymentEl.setAttribute('hidden', '');
      }
    }

    var paymentEl = form.querySelector('[data-fcpdp-payment]');

    /* Keep the struck-through compare price + "Save $X" in sync with the
       selected variant. Radios carry data-compare / data-save when a sale
       (or compare-at price) is in effect; update-only — never hide a
       server-rendered compare block when a radio omits the data. */
    function updateCompare(priceNode, compareVal, saveVal) {
      if (!priceNode) return;
      var wrap = priceNode.closest('[data-fcpdp-price-wrap]');
      if (!wrap) return;
      var strike = wrap.querySelector('[data-fcpdp-compare-strike]');
      var save = wrap.querySelector('[data-fcpdp-compare-save]');
      if (compareVal && strike) strike.textContent = compareVal;
      if (saveVal && save) save.textContent = saveVal;
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        hiddenId.value = radio.value;
        form.setAttribute('data-variant-id', radio.value);
        if (priceEl && radio.dataset.price) {
          priceEl.textContent = radio.dataset.price;
          updateCompare(priceEl, radio.dataset.compare, radio.dataset.save);
        }
        var available = radio.dataset.available === 'true';
        setSubmitState(available, radio.value);

        // Sticky bar mirroring — sync the sticky radio (without
        // re-dispatching change to avoid loops) and update the meta
        // text span using the sticky radio's short label.
        var matchingSticky = document.querySelector(
          '[data-fcpdp-sticky-variant-radio][value="' + radio.value + '"]'
        );
        if (matchingSticky && !matchingSticky.checked) matchingSticky.checked = true;
        var stickyVariant = document.querySelector('[data-fcpdp-sticky-variant]');
        var stickyPrice   = document.querySelector('[data-fcpdp-sticky-price]');
        if (stickyVariant) {
          stickyVariant.textContent = (matchingSticky && matchingSticky.dataset.variantTitle)
            || radio.dataset.variantTitle;
        }
        if (stickyPrice && radio.dataset.price) {
          stickyPrice.textContent = radio.dataset.price;
        }
      });
    });

    /* Submit click handler — when the active variant is sold out,
       prevent the form from posting and open the Stoq / Restock Rocket
       modal in place via the documented JS API. Works for every form
       (buy-box, cost-CTA, final-CTA) because the helper is shared. */
    if (submit) {
      submit.addEventListener('click', function (e) {
        if (submit.dataset.state !== 'sold-out') return;
        e.preventDefault();
        openRestockRocketModal(submit.dataset.variantId);
      });
    }
  }

  /* ---------------------------------------------------------------
     Restock Rocket / Stoq modal opener — uses the documented JS API:
     https://help.stoqapp.com/en/article/stoq-javascript-api-documentation-35g74t/

     window._RestockRocket.openModal(productData, variantId, customerData?)

     productData is exposed as window.fcpdpProductData via Liquid in
     fcpdp-buy-box.liquid (`{{ product | json }}`), giving the API
     exactly the shape it expects: { id, title, handle, variants[] }.

     If the click happens before the app's main script finishes
     loading, we listen once for `stoq:loaded` (the documented ready
     event) and retry. A 3-second hard timeout prevents the listener
     from leaking if the app never loads.
     --------------------------------------------------------------- */
  function openRestockRocketModal(variantId) {
    var product = window.fcpdpProductData;
    if (!product) return false;

    function tryOpen() {
      if (window._RestockRocket && typeof window._RestockRocket.openModal === 'function') {
        try {
          window._RestockRocket.openModal(product, String(variantId));
          return true;
        } catch (e) {}
      }
      return false;
    }

    if (tryOpen()) return true;

    var fired = false;
    var onLoaded = function () {
      if (fired) return;
      fired = true;
      tryOpen();
    };
    window.addEventListener('stoq:loaded', onLoaded, { once: true });
    setTimeout(function () {
      if (fired) return;
      fired = true;
      window.removeEventListener('stoq:loaded', onLoaded);
    }, 3000);
    return false;
  }


  /* ---------------------------------------------------------------
     Image gallery — thumbnails scroll the rail, scroll updates the
     active thumb + image counter, prev/next bounded by slide count.
     --------------------------------------------------------------- */
  function wireGallery() {
    var gallery = document.querySelector('[data-fcpdp-gallery]');
    if (!gallery) return;

    var rail    = gallery.querySelector('[data-fcpdp-gallery-rail]');
    var thumbs  = gallery.querySelectorAll('[data-fcpdp-gallery-thumb]');
    var slides  = gallery.querySelectorAll('[data-fcpdp-gallery-slide]');
    var navBtns = gallery.querySelectorAll('[data-fcpdp-gallery-nav]');
    var counter = gallery.querySelector('[data-fcpdp-gallery-counter-current]');
    if (!rail || !slides.length) return;

    function activeIndex() {
      // Closest-center beats strict containment because the rail has
      // gap:8px between slides — when scroll position lands in a gap,
      // strict containment would fall through to "return 0" mid-scroll
      // and the counter would briefly snap back to 1. Closest-center
      // always resolves to a real slide.
      var center = rail.scrollLeft + rail.clientWidth / 2;
      var closest = 0;
      var closestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var s = slides[i];
        var slideCenter = s.offsetLeft + s.offsetWidth / 2;
        var dist = Math.abs(center - slideCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      }
      return closest;
    }

    function setNavBoundsState() {
      var idx = activeIndex();
      navBtns.forEach(function (btn) {
        var step = parseInt(btn.dataset.fcpdpGalleryNav, 10) || 1;
        var atBound = (step < 0 && idx === 0) || (step > 0 && idx === slides.length - 1);
        if (atBound) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    }

    var thumbsStrip = gallery.querySelector('[data-fcpdp-gallery-thumbs]');

    function setActive(idx) {
      var activeThumb = null;
      thumbs.forEach(function (t) {
        if (parseInt(t.dataset.fcpdpGalleryThumb, 10) === idx) {
          t.setAttribute('aria-current', 'true');
          activeThumb = t;
        } else {
          t.removeAttribute('aria-current');
        }
      });
      if (counter) counter.textContent = String(idx + 1);
      setNavBoundsState();

      // Keep the active thumb roughly centered in the strip so the
      // user never loses their place when the product has many images.
      // scrollBy on the strip only — never scrollIntoView, which would
      // also scroll the page if the thumb is below the fold.
      if (activeThumb && thumbsStrip) {
        var stripRect = thumbsStrip.getBoundingClientRect();
        var thumbRect = activeThumb.getBoundingClientRect();
        var offset = (thumbRect.left - stripRect.left) - (stripRect.width / 2 - thumbRect.width / 2);
        if (Math.abs(offset) > 1) {
          thumbsStrip.scrollBy({ left: offset, behavior: 'smooth' });
        }
      }
    }

    function goTo(idx) {
      var bounded = Math.max(0, Math.min(slides.length - 1, idx));
      var slide = slides[bounded];
      if (!slide) return;
      rail.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      setActive(bounded);
    }

    /* Event delegation on the gallery — survives any DOM mutation that
       might happen if an app re-renders thumbs (cleaner than per-thumb
       listeners). Direct listeners on nav buttons stay because they
       have stable identity. */
    gallery.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-fcpdp-gallery-thumb]');
      if (thumb && gallery.contains(thumb)) {
        var idx = parseInt(thumb.dataset.fcpdpGalleryThumb, 10);
        if (!isNaN(idx)) goTo(idx);
      }
    });

    navBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        var step = parseInt(btn.dataset.fcpdpGalleryNav, 10) || 1;
        goTo(activeIndex() + step);
      });
    });

    /* Update active state ONLY after the rail finishes scrolling.
       Earlier rounds used rAF on every scroll tick, which made the
       counter race through every intermediate slide during a fast
       swipe (1 → 2 → 3 → 4 → 5 in 200ms felt like flicker). The
       debounced settle keeps the counter visually stable: it stays
       on the starting slide during the gesture, then snaps to the
       final resting slide once scroll-snap settles. ~120ms tracks
       both browser-native momentum scrolling and our smooth
       scrollTo() animations from goTo(). */
    var scrollSettleTimer = null;
    rail.addEventListener('scroll', function () {
      if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
      scrollSettleTimer = setTimeout(function () {
        scrollSettleTimer = null;
        setActive(activeIndex());
      }, 120);
    }, { passive: true });

    // Sync initial state — gives the prev arrow its disabled flag on
    // load (we start at index 0) and ensures the counter reflects
    // reality even if Liquid's default "1" gets out of step.
    // (Previously also ran an IntersectionObserver here, but the rail
    // scroll listener is already the source of truth — running both
    // raced each other and made the counter flicker.)
    setActive(activeIndex());
  }

  /* ---------------------------------------------------------------
     Image lightbox — click any gallery slide → open <dialog> with
     the full-resolution image, prev/next nav, counter, and a
     thumbnail strip. Full media list is on the dialog itself as
     JSON so we don't have to crawl the page for it.
     --------------------------------------------------------------- */
  function wireImageModal() {
    var modal = document.querySelector('[data-fcpdp-image-modal]');
    if (!modal) return;

    var img       = modal.querySelector('[data-fcpdp-image-modal-img]');
    var closeBtn  = modal.querySelector('[data-fcpdp-image-modal-close]');
    var navBtns   = modal.querySelectorAll('[data-fcpdp-image-modal-nav]');
    var counterEl = modal.querySelector('[data-fcpdp-image-modal-counter-current]');
    var thumbsEl  = modal.querySelector('[data-fcpdp-image-modal-thumbs]');

    var media = [];
    try { media = JSON.parse(modal.dataset.fcpdpImageModalMedia || '[]'); } catch (e) { media = []; }

    var current = 0;

    function goTo(idx) {
      if (!media.length) return;
      current = Math.max(0, Math.min(media.length - 1, idx));
      var item = media[current];
      if (img) {
        img.src = item.src || '';
        img.alt = item.alt || '';
      }
      if (counterEl) counterEl.textContent = String(current + 1);
      if (thumbsEl) {
        thumbsEl.querySelectorAll('[data-fcpdp-image-modal-thumb-idx]').forEach(function (b) {
          if (parseInt(b.dataset.fcpdpImageModalThumbIdx, 10) === current) {
            b.setAttribute('aria-current', 'true');
            // Scroll the thumbs strip so the active thumb stays visible
            // — scrollIntoView would also scroll the page, scrollBy on
            // the strip alone is the safe variant.
            var stripRect = thumbsEl.getBoundingClientRect();
            var thumbRect = b.getBoundingClientRect();
            var off = (thumbRect.left - stripRect.left) - (stripRect.width / 2 - thumbRect.width / 2);
            if (Math.abs(off) > 1) thumbsEl.scrollBy({ left: off, behavior: 'smooth' });
          } else {
            b.removeAttribute('aria-current');
          }
        });
      }
      navBtns.forEach(function (btn) {
        var step = parseInt(btn.dataset.fcpdpImageModalNav, 10) || 1;
        var atBound = (step < 0 && current === 0) || (step > 0 && current === media.length - 1);
        if (atBound) btn.setAttribute('aria-disabled', 'true');
        else btn.removeAttribute('aria-disabled');
      });
    }

    // Render the thumbs strip once from the JSON list. <button>s carry
    // a data-* index so click handling is event-delegated below.
    if (thumbsEl && media.length > 1) {
      var html = '';
      for (var i = 0; i < media.length; i++) {
        var src = media[i].src || '';
        // Request a smaller thumbnail variant from Shopify CDN — the
        // 1800w main URL has &width=1800; swap to 160 for the thumb.
        var thumbSrc = src.replace(/(\?|&)width=\d+/, '$1width=160');
        if (thumbSrc === src) {
          thumbSrc = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160';
        }
        html += '<li><button type="button" class="fcpdp-image-modal__thumb" data-fcpdp-image-modal-thumb-idx="' + i + '" aria-label="Show image ' + (i + 1) + '">' +
                '<img class="fcpdp-image-modal__thumb-img" src="' + thumbSrc + '" alt="" loading="lazy">' +
                '</button></li>';
      }
      thumbsEl.innerHTML = html;
    }

    function open(trigger) {
      // Find the trigger's index within the media list — match the
      // base CDN URL (Shopify rewrites width= per request, so compare
      // the path portion without query string).
      var triggerSrc = (trigger && trigger.dataset.imageSrc) || '';
      var triggerBase = triggerSrc.split('?')[0];
      var idx = 0;
      for (var i = 0; i < media.length; i++) {
        if ((media[i].src || '').split('?')[0] === triggerBase) { idx = i; break; }
      }
      goTo(idx);
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    }

    function close() {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    }

    document.querySelectorAll('[data-fcpdp-image-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        open(trigger);
      });
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    modal.addEventListener('click', function (e) {
      // Close only on true backdrop click (target is the dialog itself,
      // not any descendant). The carousel descendants stop here.
      if (e.target === modal) close();
    });

    // Nav arrows
    navBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        var step = parseInt(btn.dataset.fcpdpImageModalNav, 10) || 1;
        goTo(current + step);
      });
    });

    // Thumb strip — event delegation
    if (thumbsEl) {
      thumbsEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-fcpdp-image-modal-thumb-idx]');
        if (!btn || !thumbsEl.contains(btn)) return;
        var idx = parseInt(btn.dataset.fcpdpImageModalThumbIdx, 10);
        if (!isNaN(idx)) goTo(idx);
      });
    }

    // Keyboard nav while the dialog is open
    document.addEventListener('keydown', function (e) {
      if (!modal.hasAttribute('open')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1); }
    });
  }

  /* ---------------------------------------------------------------
     Variant-help modal — open/close <dialog>
     --------------------------------------------------------------- */
  function wireVariantHelp() {
    var triggers = document.querySelectorAll('[data-fcpdp-variant-help]');
    var modal    = document.querySelector('[data-fcpdp-variant-modal]');
    if (!triggers.length || !modal) return;

    var open = function (e) {
      e.preventDefault();
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
    };
    triggers.forEach(function (t) { t.addEventListener('click', open); });

    var closeBtn = modal.querySelector('[data-fcpdp-variant-modal-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
      });
    }
    modal.addEventListener('click', function (e) {
      if (e.target === modal) {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
      }
    });
  }

  /* ---------------------------------------------------------------
     Sticky ATC — bottom-pinned. Hidden while the buy-box is in view
     (no point duplicating the primary ATC), shown once the user
     scrolls past it, hidden again as the final-CTA conversion
     button enters the viewport (would otherwise sit on top of it).
     Replaces the Essential Sticky Cart app.
     --------------------------------------------------------------- */
  function wireSticky() {
    var bar = document.querySelector('[data-fcpdp-sticky]');
    if (!bar) return;

    var hero  = document.getElementById('fcpdp-hero');     /* buy-box section */
    var final = document.getElementById('fcpdp-final-cta');
    if (!hero || !('IntersectionObserver' in window)) return;

    bar.removeAttribute('hidden');

    var heroPastFold = false;
    var finalInView  = false;
    function update() {
      var visible = (heroPastFold && !finalInView);
      bar.dataset.visible = visible ? 'true' : 'false';
      // Toggle a body class so the page can reserve bottom padding
      // without relying on :has() (older browsers don't support it).
      document.body.classList.toggle('fcpdp-sticky-active', visible);
    }

    var heroIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        heroPastFold = !entry.isIntersecting;
        update();
      });
    }, { rootMargin: '-72px 0px 0px 0px', threshold: 0 });
    heroIO.observe(hero);

    if (final) {
      var finalIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          finalInView = entry.isIntersecting;
          update();
        });
      }, { threshold: 0.1 });
      finalIO.observe(final);
    }

    var stickyCta = bar.querySelector('[data-fcpdp-sticky-cta]');
    if (stickyCta) {
      stickyCta.addEventListener('click', function (e) {
        var target = document.getElementById('fcpdp-hero-buy');
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        var firstRadio = target.querySelector('[data-fcpdp-variant-radio]:not([disabled])');
        if (firstRadio) firstRadio.focus({ preventScroll: true });
      });
    }

    /* Sticky variant pills — when the user changes a variant from the
       sticky bar, mirror the selection into the hero form's radio so
       the existing change handler cascades price / hidden id /
       accelerated checkout / OOS Stoq state across the page. */
    bar.querySelectorAll('[data-fcpdp-sticky-variant-radio]').forEach(function (stickyRadio) {
      stickyRadio.addEventListener('change', function () {
        if (!stickyRadio.checked) return;
        var heroForm = document.querySelector('[data-fcpdp-buy-block][data-id-prefix="hero"]');
        if (!heroForm) return;
        var heroRadio = heroForm.querySelector('[data-fcpdp-variant-radio][value="' + stickyRadio.value + '"]');
        if (!heroRadio) return;
        heroRadio.checked = true;
        heroRadio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ---------------------------------------------------------------
     Mirror fc-shipping-day output into all SHIPS TODAY/SHIPS TOMORROW
     surfaces (homepage script only auto-injects one ID).
     --------------------------------------------------------------- */
  function wireShippingMirrors() {
    if (!window.FCShipping) return;
    var text = window.FCShipping.getText();
    document.querySelectorAll('[id^="fc-eyebrow-ship-day"], .fcpdp-buy-block__meta-ship').forEach(function (el) {
      // The homepage script writes uppercase; mixed-case inline labels
      // ("Ships today") get matched normalized.
      el.textContent = el.classList.contains('fcpdp-buy-block__meta-ship')
        ? text.charAt(0) + text.slice(1).toLowerCase()
        : text;
    });
  }

  /* Patch the Shopify preview-bar iframe with a title (a11y — Lighthouse
     flags it because Shopify injects the iframe without one). */
  function patchPreviewBarA11y() {
    var pb = document.getElementById('PBarNextFrame');
    if (pb && !pb.hasAttribute('title')) pb.setAttribute('title', 'Shopify preview bar');
  }

  function init() {
    document.querySelectorAll('[data-fcpdp-buy-block]').forEach(wireBuyBlock);
    wireGallery();
    wireImageModal();
    wireVariantHelp();
    wireSticky();
    patchPreviewBarA11y();
    // Run shipping mirrors after FCShipping has had a tick to load
    setTimeout(wireShippingMirrors, 0);
    // Preview bar may be injected after our init runs
    setTimeout(patchPreviewBarA11y, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
